(function () {
  'use strict';

  const ASSESSMENT_STORAGE_KEY = 'taraConsultationAssessment';
  const DISMISSED_CODE_STORAGE_KEY = 'taraConsultationDismissedOfferCode';
  const SYNC_DELAY_MS = 600;
  const APPROVED_FULL_SET_DISCOUNT_PERCENT = 20;
  const APPROVED_FULL_SET_OFFER_VERSION = 'tara-us-system-20-v1';
  const APPROVED_FULL_SET_CODE_PATTERN = /^TC[A-Z0-9]{5}$/;

  let syncTimer = null;
  let syncInFlight = null;

  function parseJson(rawValue, fallback) {
    if (!rawValue) return fallback;
    try {
      return JSON.parse(rawValue);
    } catch (error) {
      return fallback;
    }
  }

  function normalizeKey(value) {
    let normalized = (value || '').toString().toLowerCase().trim();
    if (normalized.normalize) {
      normalized = normalized
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/g, '');
    }

    return normalized
      .replace(/[_/+]+/g, ' ')
      .replace(/-/g, ' ')
      .replace(/[^\w\u0600-\u06FF ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getThemeCartUpdateUrl() {
    return window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url
      ? window.Theme.routes.cart_update_url
      : '/cart/update';
  }

  function readAssessmentRecord() {
    try {
      return parseJson(window.sessionStorage.getItem(ASSESSMENT_STORAGE_KEY), null);
    } catch (error) {
      return null;
    }
  }

  function getAssessmentPayload() {
    const record = readAssessmentRecord();
    if (!record || typeof record !== 'object') return null;
    return record.assessment && typeof record.assessment === 'object'
      ? record.assessment
      : record;
  }

  function getDismissedCode() {
    try {
      return (window.sessionStorage.getItem(DISMISSED_CODE_STORAGE_KEY) || '').trim();
    } catch (error) {
      return '';
    }
  }

  function setDismissedCode(code) {
    try {
      if (!code) {
        window.sessionStorage.removeItem(DISMISSED_CODE_STORAGE_KEY);
        return;
      }
      window.sessionStorage.setItem(DISMISSED_CODE_STORAGE_KEY, code);
    } catch (error) {
      /* no-op */
    }
  }

  function clearDismissedCodeFor(code) {
    if (!code) return;
    if (getDismissedCode() === code) {
      setDismissedCode('');
    }
  }

  function getOfferFromAssessment() {
    const assessment = getAssessmentPayload();
    const offer = assessment && assessment.offer && typeof assessment.offer === 'object'
      ? assessment.offer
      : null;
    if (!offer) return null;

    const activation = offer.activation && typeof offer.activation === 'object' ? offer.activation : null;
    const code = (
      (activation && activation.code)
      || offer.code
      || offer.discount_code
      || ''
    ).toString().trim();
    const status = (
      (activation && activation.status)
      || offer.activation_status
      || ''
    ).toString().trim().toLowerCase();
    const discountPercent = Number(offer.discount_percent || offer.discountPercent) || 0;
    const policyVersion = (offer.policy_version || offer.policyVersion || '').toString().trim();
    const expiresAtRaw = (
      (activation && activation.expires_at)
      || offer.expires_at
      || ''
    ).toString().trim();
    const expiresAtMs = expiresAtRaw ? Date.parse(expiresAtRaw) : NaN;
    const notExpired = Number.isFinite(expiresAtMs) && expiresAtMs > Date.now();

    if (
      !APPROVED_FULL_SET_CODE_PATTERN.test(code)
      || status !== 'active'
      || discountPercent !== APPROVED_FULL_SET_DISCOUNT_PERCENT
      || policyVersion !== APPROVED_FULL_SET_OFFER_VERSION
      || !notExpired
    ) return null;

    return {
      code,
      discountPercent,
      policyVersion,
      expiresAt: expiresAtRaw,
      mode: normalizeKey(offer.mode || ''),
      bundleHandle: normalizeKey(offer.bundle_handle || offer.bundleHandle || ''),
      requiredHandles: Array.isArray(offer.required_handles)
        ? offer.required_handles.map(normalizeKey).filter(Boolean)
        : [],
      requiredVariantIds: Array.isArray(offer.required_variant_ids)
        ? offer.required_variant_ids
          .map(function mapVariantId(value) { return Number(value); })
          .filter(function filterVariantId(value) { return Number.isFinite(value) && value > 0; })
        : [],
    };
  }

  function getCurrentDiscountCodes() {
    return Array.from(document.querySelectorAll('.cart-discount__pill[data-discount-code]'))
      .map(function mapPill(pill) {
        return pill instanceof HTMLElement ? (pill.dataset.discountCode || '').trim() : '';
      })
      .filter(Boolean);
  }

  function getDiscountCodesForSection(sectionId) {
    const normalizedSectionId = (sectionId || '').toString().trim();
    if (!normalizedSectionId) return [];

    const component = document.querySelector(`cart-items-component[data-section-id="${normalizedSectionId}"]`);
    if (!(component instanceof HTMLElement)) return [];

    return Array.from(component.querySelectorAll('.cart-discount__pill[data-discount-code]'))
      .map(function mapPill(pill) {
        return pill instanceof HTMLElement ? (pill.dataset.discountCode || '').trim() : '';
      })
      .filter(Boolean);
  }

  function getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map(function mapComponent(component) {
        return component instanceof HTMLElement ? (component.dataset.sectionId || '').trim() : '';
      })
      .filter(Boolean);
  }

  function getSectionsMissingOfferCode(sectionIds, offerCode) {
    return (sectionIds || []).filter(function filterSection(sectionId) {
      return !getDiscountCodesForSection(sectionId).includes(offerCode);
    });
  }

  function cartMatchesOffer(cart, offer) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length || !offer) return false;

    const cartHandles = new Set(
      cart.items
        .map(function mapHandle(item) {
          return normalizeKey(item && item.handle);
        })
        .filter(Boolean)
    );
    const cartVariantIds = new Set(
      cart.items
        .map(function mapVariantId(item) {
          return Number(item && item.variant_id);
        })
        .filter(function filterVariantId(value) {
          return Number.isFinite(value) && value > 0;
        })
    );

    if (offer.requiredVariantIds.length) {
      return offer.requiredVariantIds.every(function everyVariantId(value) {
        return cartVariantIds.has(value);
      });
    }

    if (offer.bundleHandle) {
      return cartHandles.has(offer.bundleHandle);
    }

    if (offer.requiredHandles.length) {
      return offer.requiredHandles.every(function everyHandle(value) {
        return cartHandles.has(value);
      });
    }

    return false;
  }

  async function fetchCartState() {
    const response = await window.fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error('Cart state request failed');
    }
    return response.json();
  }

  function buildDiscountUpdateEvent(detail) {
    return new CustomEvent('discount:update', {
      bubbles: true,
      detail: {
        resource: detail || null,
        sourceId: 'tara-consultation-cart-offer-sync',
        data: {
          source: 'tara-consultation-cart-offer-sync',
          sections: detail && typeof detail.sections === 'object' ? detail.sections : {},
        },
      },
    });
  }

  function afterUiFlush() {
    return new Promise(function afterFlush(resolve) {
      window.requestAnimationFrame(function onFrame() {
        window.requestAnimationFrame(resolve);
      });
    });
  }

  async function revealAppliedDiscounts() {
    await afterUiFlush();

    document.querySelectorAll('cart-discount-component').forEach(function reveal(component) {
      if (!(component instanceof HTMLElement)) return;
      if (!component.querySelector('.cart-discount__pill')) return;

      const disclosure = component.closest('details');
      if (disclosure instanceof HTMLDetailsElement) {
        disclosure.open = true;
      }
    });
  }

  async function applyDiscountCode(code, sectionIds) {
    const response = await window.fetch(getThemeCartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        discount: code,
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error((data && data.description) || 'Discount update failed');
    }

    return data;
  }

  async function syncConsultationOffer(reason) {
    const offer = getOfferFromAssessment();
    if (!offer) return null;

    if (getDismissedCode() === offer.code) return null;

    const sectionIds = getCartSectionIds();
    if (!sectionIds.length) return null;

    const staleSectionIds = getSectionsMissingOfferCode(sectionIds, offer.code);
    if (!staleSectionIds.length) {
      clearDismissedCodeFor(offer.code);
      return null;
    }

    const cart = await fetchCartState();
    if (!cartMatchesOffer(cart, offer)) return null;

    const data = await applyDiscountCode(offer.code, sectionIds);
    document.dispatchEvent(buildDiscountUpdateEvent(data));
    await revealAppliedDiscounts();
    clearDismissedCodeFor(offer.code);

    return {
      reason,
      code: offer.code,
      sections: sectionIds,
      response: data,
    };
  }

  function syncNow(reason) {
    if (syncInFlight) return syncInFlight;

    syncInFlight = syncConsultationOffer(reason)
      .catch(function handleError(error) {
        console.error('[Tara Consultation] Cart offer sync failed:', error);
        return null;
      })
      .finally(function clearInflight() {
        syncInFlight = null;
      });

    return syncInFlight;
  }

  function scheduleSync(reason) {
    if (syncTimer) {
      window.clearTimeout(syncTimer);
    }
    syncTimer = window.setTimeout(function runScheduledSync() {
      syncTimer = null;
      void syncNow(reason);
    }, SYNC_DELAY_MS);
  }

  function handleDiscountRemoval(event) {
    const trigger = event.target instanceof HTMLElement
      ? event.target.closest('.cart-discount__pill-remove')
      : null;
    if (!(trigger instanceof HTMLElement)) return;

    const pill = trigger.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLElement)) return;

    const offer = getOfferFromAssessment();
    const removedCode = (pill.dataset.discountCode || '').trim();
    if (!offer || !removedCode || removedCode !== offer.code) return;

    setDismissedCode(removedCode);
  }

  function boot() {
    document.addEventListener('click', handleDiscountRemoval, true);
    document.addEventListener('cart:update', function onCartUpdate() {
      scheduleSync('cart:update');
    });
    window.addEventListener('pageshow', function onPageShow() {
      scheduleSync('pageshow');
    });
    scheduleSync('boot');
  }

  window.TaraConsultationCartOfferSync = Object.freeze({
    syncNow,
    scheduleSync,
    getOfferFromAssessment,
    cartMatchesOffer,
    getCurrentDiscountCodes,
    getDiscountCodesForSection,
    getCartSectionIds,
    getSectionsMissingOfferCode,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
